import { NOT_ASSIGNED } from 'constants/constants';
import React from 'react';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { EPDProductInformationModel, ProductStatusEnum } from 'types/types';
import VersionHistorySummaryGrid, { SummaryGridRow } from '../VersionHistorySummaryGrid';
import usePcrV2 from '../../hooks/usePcrV2';
import { EpdVersionHistoryData, useEpdVersionHistoryData } from '../utils/EpdVersionHistoryDataContext';
import { getDisplayRegionName } from 'pages/v2/compiler/PDF/utils/getDisplayRegionName';

const VersionHistoryProductInformationGrid: React.FC<{ product?: EPDProductInformationModel }> = ({ product }) => {
  const epdVersionHistoryData = useEpdVersionHistoryData();
  const isPcrV2 = usePcrV2();

  const rows =
    epdVersionHistoryData && product ? getProductInformationGridRows(epdVersionHistoryData, isPcrV2, product) || [] : [];
  return <VersionHistorySummaryGrid rows={rows} />;
};

export default VersionHistoryProductInformationGrid;

function formatOptionalBooleanAsYesNoOrNotAssigned(flag: boolean | null | undefined): string {
  if (typeof flag !== 'boolean') {
    return NOT_ASSIGNED;
  }
  return flag ? 'Yes' : 'No';
}

export const getProductInformationGridRows = (
  data: EpdVersionHistoryData,
  isPcrV2: boolean,
  product?: EPDProductInformationModel
): SummaryGridRow[] => {
  const { pcrInformation, countries, dictionaries, cpcCodes, materialPropertiesLookup } = data || {};

  if (!product) {
    return [];
  }

  const possibleRows: (SummaryGridRow | false)[] = [
    {
      ord: 1,
      name: 'Product name',
      value: product.productName ?? NOT_ASSIGNED,
    },
    isPcrV2 && {
      ord: 2,
      name: 'Product status',
      value:
        dictionaries.find(
          (d) =>
            d.dictionaryCode === EPD_DICTIONARY_CODES.PRODUCT_STATUSES &&
            d.key === Object.keys(ProductStatusEnum).indexOf(product.productStatus as string)
        )?.value ?? NOT_ASSIGNED,
    },
    !!pcrInformation.pcr?.isCPCR22OrCPCR23Selected && {
      ord: 3,
      name: 'Is the project in the planning/design phase?',
      value: formatOptionalBooleanAsYesNoOrNotAssigned(product.isTheProjectInThePlanningOrDesignPhase),
    },
    {
      ord: 4,
      name: 'Product identification',
      value: product.productIdentification ?? NOT_ASSIGNED,
    },
    {
      ord: 5,
      name: 'Product description',
      value: product.productDescription ?? NOT_ASSIGNED,
    },
    !!product.productInformationExternal && {
      ord: 6,
      name: 'Product information from external sources',
      value: product.productInformationExternal,
    },
    {
      ord: 7,
      name: 'Technical purpose of product',
      value: product.technicalPurposeOfProduct ?? NOT_ASSIGNED,
    },
    {
      ord: 8,
      name: 'Manufacturing or service provision description',
      value: product.manufacturingOrServiceProvisionDescription ?? NOT_ASSIGNED,
    },
    !!product.productMaterialProperties &&
      product.productMaterialProperties.length > 0 && {
        ord: 9,
        name: 'Material properties',
        value: product.productMaterialProperties
          ?.map((mp) => {
            const property = materialPropertiesLookup.find((m) => m.id === mp.materialPropertyId);
            return property ? `Unit: ${property.fullName}\nValue: ${mp.value}` : NOT_ASSIGNED;
          })
          .join('\n\n'),
        isHtmlRichText: true,
      },
    {
      ord: 10,
      name: isPcrV2 ? 'Manufacturing sites' : 'Production sites',
      value:
        product.productionSites
          ?.map((s) => {
            const details = [
              s.nameOfManufacturer,
              s.name,
              s.addressLine,
              s.addressLineSecond,
              [s.zipCode, s.city].filter(Boolean).join(' '),
              getDisplayRegionName(s.regionId, data.regions),
              countries.find((c) => c.id === s.countryId)?.name,
            ]
              .filter((x) => x)
              .join('\n');
            return details || NOT_ASSIGNED;
          })
          .join('\n\n') ?? NOT_ASSIGNED,
    },
    {
      ord: 11,
      name: 'UN CPC code',
      value: product.isUNCPCCodeNotApplicable
        ? 'No applicable UN CPC code'
        : cpcCodes.find((cpc) => cpc.id === product.uncpcId)?.description ?? NOT_ASSIGNED,
    },
    {
      ord: 12,
      name: 'GTIN',
      value: product.gtin ?? NOT_ASSIGNED,
    },
    !!product.productClassifications &&
      product.productClassifications.length > 0 && {
        ord: 13,
        name: 'Product classification system',
        value: product.productClassifications
          .map((pc) => {
            const classification =
              dictionaries.find(
                (d) =>
                  d.dictionaryCode === EPD_DICTIONARY_CODES.CLASSIFICATION_CODE && d.key === pc.productClassificationType
              )?.value ?? NOT_ASSIGNED;
            return [classification, pc.description, pc.code].filter(Boolean).join('\n');
          })
          .join('\n\n'),
      },
    {
      ord: 14,
      name: 'Geographical scope',
      value: product.productGeographicalScopes?.map((g) => g.name)?.join(', ') ?? NOT_ASSIGNED,
    },
    {
      ord: 15,
      name: 'Geographical scope description',
      value: product.geographicalScopeDescription ?? NOT_ASSIGNED,
    },
    {
      ord: 16,
      name: 'Actual or technical lifespan',
      value: (() => {
        const value = product.unitValue;
        const unit = dictionaries.find(
          (d) => d.dictionaryCode === EPD_DICTIONARY_CODES.UNIT_TYPE && d.key === product.unitType
        )?.value;
        if (!value || !unit) {
          return NOT_ASSIGNED;
        }
        return `${value} ${unit.toLowerCase()}`;
      })(),
    },
  ];

  return possibleRows.filter(Boolean) as SummaryGridRow[];
};
